import React, { useState, useCallback, useEffect } from 'react';
import { formatText } from '../utils/formatText';

const LONG_VALUE_LIMIT = 420;

function displayValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.map((item) => displayValue(item)).join(', ');
  return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
}

const IMAGE_URL = /\.(png|jpe?g|gif|webp|svg)(?:[?#].*)?$/i;
const FILE_URL = /\.(pdf|xlsx?|csv|docx?|pptx?)(?:[?#].*)?$/i;

function fileNameFromUrl(url) {
  try { return decodeURIComponent(new URL(url).pathname.split('/').pop() || 'Download file'); } catch { return 'Download file'; }
}

function RowCopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const handle = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);
  return (
    <div className="row-copy-wrap">
      <button className={`row-copy-btn${copied ? ' is-copied' : ''}`} onClick={handle} aria-label="Copy value">
        {copied
          ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><path d="M20 6L9 17l-5-5" /></svg>
          : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
        }
      </button>
      <span className="row-copy-tip">{copied ? 'Copied!' : 'Copy'}</span>
    </div>
  );
}

function DetailModal({ label, value, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="detail-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div className="detail-modal" role="dialog" aria-modal="true" aria-labelledby="detail-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="detail-modal__head">
          <div>
            <span className="detail-modal__eyebrow">Full details</span>
            <h2 id="detail-modal-title">{label}</h2>
          </div>
          <button type="button" className="detail-modal__close" onClick={onClose} aria-label="Close full details">×</button>
        </div>
        <div className="detail-modal__content">{value}</div>
        <div className="detail-modal__foot">
          <button type="button" className="detail-modal__copy" onClick={copy}>{copied ? 'Copied!' : 'Copy details'}</button>
          <button type="button" className="detail-modal__done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ResultValue({ label, value }) {
  const [showDetails, setShowDetails] = useState(false);
  if (/^https?:\/\//i.test(value)) {
    if (IMAGE_URL.test(value)) {
      return <a className="api-result__image-link" href={value} target="_blank" rel="noreferrer"><img src={value} alt={label} onError={(event) => { event.currentTarget.style.display = 'none'; }} /></a>;
    }
    if (FILE_URL.test(value)) {
      return <a className="api-result__file-link" href={value} target="_blank" rel="noreferrer" download><span>⇩</span> Download {fileNameFromUrl(value)}</a>;
    }
    return <a className="api-result__file-link" href={value} target="_blank" rel="noreferrer"><span>↗</span> Open link</a>;
  }
  const isLong = value.length > LONG_VALUE_LIMIT;
  const preview = isLong ? `${value.slice(0, LONG_VALUE_LIMIT).trimEnd()}…` : value;

  return (
    <>
      <div className={`api-result__preview${isLong ? ' is-truncated' : ''}`}>{preview}</div>
      {isLong && <button type="button" className="api-result__details-btn" onClick={() => setShowDetails(true)}>View Detail</button>}
      {showDetails && <DetailModal label={label} value={value} onClose={() => setShowDetails(false)} />}
    </>
  );
}

export default function Message({
  role,
  text,
  source,
  loading,
  related,
  heading,
  table,
  result,
  columns,
  onSelect,
  disabled,
}) {
  const isUser = role === 'user';

  if (isUser) {
    return (
      <div className="msg-row msg-row--user">
        <div className="msg-bubble msg-bubble--user">
          <div className="msg-text">{formatText(text)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="msg-row msg-row--bot">
      <div className="msg-avatar" aria-hidden="true">
        <span>CR</span>
      </div>
      <div className="msg-col">
        <div className="msg-bubble msg-bubble--bot">
          {loading ? (
            <div className="typing" aria-label="Assistant is typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          ) : (
            <>
              <div className="msg-text">{formatText(text)}</div>

              {table && table.length > 0 && (
                <div className="datasheet">
                  {heading && (
                    <div className="datasheet__title">{heading}</div>
                  )}
                  <div className="datasheet__grid">
                    {table.map((f, idx) => (
                      <div
                        key={idx}
                        className={`field ${f.wide ? 'field--wide' : ''}`}
                      >
                        <div className="field__label">{f.label}</div>
                        <div className="field__value">{f.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result && (
                <div className="api-result-list">
                  {(Array.isArray(result) ? result : [result]).map((row, resultIndex) => {
                    const fields = columns.length
                      ? columns.map((column) => ({ key: column.key, label: column.displayName }))
                      : Object.keys(row || {}).map((key) => ({ key, label: key.replace(/_/g, ' ') }));
                    return (
                      <div className="api-result-wrap" key={resultIndex}>
                        {Array.isArray(result) && <div className="api-result__record">Result {resultIndex + 1}</div>}
                        <table className="api-result"><tbody>{fields.map((field) => {
                          const val = displayValue(row?.[field.key]);
                          return (
                            <tr key={field.key} className="api-result__row">
                              <th scope="row"><div className="api-result__cell">{field.label}</div></th>
                              <td>
                                <div className="api-result__cell api-result__cell--value">
                                  <ResultValue label={field.label} value={val} />
                                  <RowCopyBtn value={val} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}</tbody></table>
                      </div>
                    );
                  })}
                </div>
              )}

              {source && (
                <div className="msg-source">
                  <span className="msg-source__icon" aria-hidden="true">◆</span>
                  Source: {source}
                </div>
              )}
            </>
          )}
        </div>

        {!loading && related && related.length > 0 && (
          <div className="recos">
            <div className="recos__title">
              <span className="recos__spark" aria-hidden="true">✦</span>
              AI Recommended Key Questions
            </div>
            <div className="recos__list">
              {related.map((r) => (
                <button
                  key={r.id}
                  className="reco"
                  onClick={() => onSelect && onSelect(r)}
                  disabled={disabled}
                >
                  <span className="reco__q">{r.displayQuestion}</span>
                  <span className="reco__arrow" aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
